import React, { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Bell, CalendarClock, FileText, Plus, Send, Trash2, X } from "lucide-react";
import { SlotPicker } from "../../components/SlotPicker";
import { rhm, apiError, money, dt } from "../api";
import { useRhmAuth } from "../RhmAuthContext";
import { Badge, Btn, Card, Field, Input, Select, StatCard, Table, Td, Textarea } from "../ui";

const TABS = ["bookings", "availability", "notifications"];
const WEEKDAYS = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"];
const STATUS = ["scheduled", "completed", "cancelled"];
const PAYMENTS = ["pending", "paid", "waived", "refunded"];

const statusTone = (s) => ({ scheduled: "gold", completed: "good", cancelled: "danger" }[s] || "muted");

export default function RhmBookings() {
    const { can } = useRhmAuth();
    const qc = useQueryClient();
    const [tab, setTab] = useState("bookings");
    const [filters, setFilters] = useState({ status: "", q: "" });
    const [openId, setOpenId] = useState(null);

    const { data: summary = {} } = useQuery({
        queryKey: ["consult-summary"],
        queryFn: async () => (await rhm.get("/consult/summary")).data,
        refetchInterval: 60000,
    });
    const { data: bookings = [] } = useQuery({
        queryKey: ["consult-bookings", filters],
        queryFn: async () => (await rhm.get("/consult/bookings", { params: { status: filters.status || undefined, q: filters.q || undefined } })).data,
    });

    const refresh = () => {
        qc.invalidateQueries({ queryKey: ["consult-bookings"] });
        qc.invalidateQueries({ queryKey: ["consult-summary"] });
    };

    return (
        <div className="space-y-6" data-testid="rhm-bookings-page">
            <div>
                <h1 className="font-display text-3xl tracking-tight">Consultation Desk</h1>
                <p className="text-sm text-zinc-500 mt-1">Appointments, patient intake, clinical notes and consultant availability.</p>
            </div>

            <div className="grid grid-cols-2 lg:grid-cols-6 gap-3">
                <StatCard label="TODAY" value={summary.today ?? "—"} tone="gold" testId="stat-today" />
                <StatCard label="UPCOMING" value={summary.upcoming ?? "—"} testId="stat-upcoming" />
                <StatCard label="INTAKE PENDING" value={summary.intake_pending ?? "—"} tone="danger" testId="stat-intake" />
                <StatCard label="PAYMENT PENDING" value={summary.payment_pending ?? "—"} tone="danger" testId="stat-payment" />
                <StatCard label="COMPLETED" value={summary.completed ?? "—"} tone="good" testId="stat-completed" />
                <StatCard label="TOTAL" value={summary.total ?? "—"} testId="stat-total" />
            </div>

            <div className="flex gap-1 border-b border-white/10">
                {TABS.map((t) => (
                    <button key={t} onClick={() => setTab(t)} data-testid={`bookings-tab-${t}`}
                        className={`mono text-[10px] tracking-[0.18em] px-4 py-3 border-b-2 transition-colors ${
                            tab === t ? "border-gold text-gold" : "border-transparent text-zinc-500 hover:text-white"
                        }`}>{t.toUpperCase()}</button>
                ))}
            </div>

            {tab === "bookings" && (
                <Card title={`Consultations (${bookings.length})`} action={
                    <div className="flex gap-2">
                        <Select value={filters.status} onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value }))}
                            data-testid="filter-status" options={[{ value: "", label: "All statuses" }, ...STATUS.map((s) => ({ value: s, label: s }))]} />
                        <Input placeholder="Search ID / name / phone" value={filters.q} data-testid="filter-search"
                            onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))} />
                    </div>
                }>
                    <Table head={["Consultation", "Patient", "Consultant", "Appointment", "Intake", "Payment", "Status", ""]}
                        testId="bookings-table"
                        empty={bookings.length === 0 && <div className="py-10 text-center text-sm text-zinc-500">No consultations yet.</div>}>
                        {bookings.map((b) => (
                            <tr key={b.id} data-testid={`booking-row-${b.consultation_id}`}>
                                <Td><span className="mono text-[11px] text-gold">{b.consultation_id}</span>
                                    <div className="text-xs text-zinc-500">{b.package_name}</div></Td>
                                <Td>{b.patient_name}<div className="text-xs text-zinc-500">{b.patient_phone}</div></Td>
                                <Td>{b.doctor_name}</Td>
                                <Td>{b.slot_label}</Td>
                                <Td><Badge tone={b.intake_status === "submitted" ? "good" : "warn"}>{(b.intake_status || "pending").toUpperCase()}</Badge></Td>
                                <Td><Badge tone={b.payment_status === "paid" ? "good" : "warn"}>{(b.payment_status || "pending").toUpperCase()}</Badge>
                                    <div className="text-xs text-zinc-500 mt-1">{money(b.amount)}</div></Td>
                                <Td><Badge tone={statusTone(b.status)}>{b.status.toUpperCase()}</Badge></Td>
                                <Td><Btn variant="ghost" onClick={() => setOpenId(b.id)} data-testid={`open-booking-${b.consultation_id}`}>OPEN</Btn></Td>
                            </tr>
                        ))}
                    </Table>
                </Card>
            )}

            {tab === "availability" && <AvailabilityPanel canManage={can("consultations.manage")} />}
            {tab === "notifications" && <NotificationsPanel />}

            {openId && <Workspace bookingId={openId} onClose={() => { setOpenId(null); refresh(); }} can={can} />}
        </div>
    );
}

// ---------------------------------------------------------------- workspace
const Workspace = ({ bookingId, onClose, can }) => {
    const [state, setState] = useState(null);
    const [clinical, setClinical] = useState({});
    const [messages, setMessages] = useState([]);
    const [draft, setDraft] = useState("");
    const [slot, setSlot] = useState(null);

    const load = async () => {
        const { data } = await rhm.get(`/consult/bookings/${bookingId}`);
        setState(data);
        setClinical(data.booking.clinical || {});
        setSlot({ slot_date: data.booking.slot_date, slot_time: data.booking.slot_time });
    };
    const loadMessages = async () => {
        try { setMessages((await rhm.get(`/consult/bookings/${bookingId}/messages`)).data); } catch { /* no perm */ }
    };

    useEffect(() => { load().catch((e) => toast.error(apiError(e))); loadMessages(); }, [bookingId]);
    useEffect(() => { const t = setInterval(loadMessages, 15000); return () => clearInterval(t); }, [bookingId]);

    if (!state) return null;
    const b = state.booking;
    const intake = b.intake || {};

    const patch = async (body) => {
        try { const { data } = await rhm.patch(`/consult/bookings/${bookingId}`, body); setState((s) => ({ ...s, booking: data })); toast.success("Updated"); }
        catch (e) { toast.error(apiError(e)); }
    };
    const saveClinical = async () => {
        try { await rhm.put(`/consult/bookings/${bookingId}/clinical`, clinical); toast.success("Clinical notes saved"); }
        catch (e) { toast.error(apiError(e)); }
    };
    const doReschedule = async () => {
        if (!slot?.slot_date || !slot?.slot_time) return toast.error("Pick a new date and time first");
        try { const { data } = await rhm.post(`/consult/bookings/${bookingId}/reschedule`, slot); setState((s) => ({ ...s, booking: data })); toast.success("Rescheduled"); }
        catch (e) { toast.error(apiError(e)); }
    };
    const send = async () => {
        if (!draft.trim()) return;
        try { await rhm.post(`/consult/bookings/${bookingId}/messages`, { body: draft }); setDraft(""); loadMessages(); }
        catch (e) { toast.error(apiError(e)); }
    };
    const openDoc = async (d) => {
        try {
            const res = await rhm.get(`/consult/documents/${d.id}/file`, { responseType: "blob" });
            const url = URL.createObjectURL(res.data);
            window.open(url, "_blank", "noopener");
            setTimeout(() => URL.revokeObjectURL(url), 60000);
        } catch (e) { toast.error(apiError(e)); }
    };

    const answers = (obj) => Object.entries(obj || {}).filter(([, v]) => v !== "" && v !== null && v !== undefined);

    return (
        <div className="fixed inset-0 z-[80] bg-black/85 backdrop-blur-sm overflow-y-auto" data-testid="booking-workspace">
            <div className="min-h-full flex justify-end">
                <div className="w-full max-w-3xl bg-bg0 border-l border-white/12 p-6 space-y-6">
                    <div className="flex items-start justify-between gap-4">
                        <div>
                            <div className="mono text-[10px] tracking-[0.2em] text-gold">{b.consultation_id}</div>
                            <h2 className="font-display text-3xl tracking-tight mt-1">{b.patient_name}</h2>
                            <div className="text-sm text-zinc-400 mt-1">
                                {b.package_name} · {b.doctor_name} · {b.slot_label} IST
                            </div>
                            <div className="text-xs text-zinc-500 mt-1">{b.customer_email} · {b.patient_phone}
                                {b.patient_age ? ` · ${b.patient_age} yrs` : ""}{b.patient_gender ? ` · ${b.patient_gender}` : ""}
                                {b.patient_city ? ` · ${b.patient_city}` : ""}</div>
                        </div>
                        <button onClick={onClose} data-testid="workspace-close"
                            className="w-9 h-9 border border-white/15 flex items-center justify-center text-zinc-400 hover:text-white">
                            <X className="w-4 h-4" />
                        </button>
                    </div>

                    <Card title="Status & payment">
                        <div className="grid sm:grid-cols-3 gap-4">
                            <Field label="Consultation status">
                                <Select value={b.status} data-testid="workspace-status" options={STATUS}
                                    onChange={(e) => patch({ status: e.target.value })} />
                            </Field>
                            <Field label="Payment status" hint={money(b.amount)}>
                                <Select value={b.payment_status || "pending"} data-testid="workspace-payment" options={PAYMENTS}
                                    onChange={(e) => patch({ payment_status: e.target.value })} />
                            </Field>
                            <Field label="Payment reference">
                                <Input defaultValue={b.payment_ref || ""} data-testid="workspace-payment-ref"
                                    onBlur={(e) => e.target.value !== (b.payment_ref || "") && patch({ payment_ref: e.target.value })} />
                            </Field>
                        </div>
                        <div className="mt-6 border-t border-white/10 pt-5">
                            <div className="mono text-[10px] tracking-[0.2em] text-zinc-500 mb-3">MOVE APPOINTMENT</div>
                            <SlotPicker doctorId={b.doctor_id} value={slot} onChange={setSlot} testPrefix="workspace-slot" />
                            <div className="mt-5">
                                <Btn variant="ghost" onClick={doReschedule} data-testid="workspace-reschedule">
                                    <CalendarClock className="w-3.5 h-3.5 inline mr-2" /> RESCHEDULE
                                </Btn>
                            </div>
                        </div>
                    </Card>

                    {intake && (
                        <Card title={`Patient intake — ${(b.intake_status || "pending").toUpperCase()}`}>
                            {["basics", "concern", "questions", "history", "consent"].map((sec) => (
                                answers(intake[sec]).length > 0 && (
                                    <div key={sec} className="mb-5" data-testid={`intake-section-${sec}`}>
                                        <div className="mono text-[10px] tracking-[0.2em] text-zinc-500 mb-2">{sec.toUpperCase()}</div>
                                        <div className="grid sm:grid-cols-2 gap-x-6 gap-y-2">
                                            {answers(intake[sec]).map(([k, v]) => (
                                                <div key={k} className="text-sm">
                                                    <span className="text-zinc-500">{k.replace(/_/g, " ")}: </span>
                                                    <span className="text-white">{String(v)}</span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )
                            ))}
                            {b.notes && <div className="text-sm text-zinc-300 border-t border-white/10 pt-4">Booking note: {b.notes}</div>}
                        </Card>
                    )}

                    {can("documents.view") && (
                        <Card title={`Documents (${(state.documents || []).length})`}>
                            {(state.documents || []).length === 0
                                ? <div className="text-sm text-zinc-500">No documents uploaded.</div>
                                : <ul className="space-y-2">
                                    {state.documents.map((d) => (
                                        <li key={d.id} className="flex items-center gap-3 border border-white/10 px-4 py-3">
                                            <FileText className="w-4 h-4 text-gold" />
                                            <span className="text-sm flex-1 truncate">{d.original_filename}</span>
                                            <span className="text-xs text-zinc-500">{dt(d.created_at)}</span>
                                            <Btn variant="ghost" onClick={() => openDoc(d)} data-testid={`workspace-doc-${d.id}`}>VIEW</Btn>
                                        </li>
                                    ))}
                                </ul>}
                        </Card>
                    )}

                    {can("clinical.edit") && (
                        <Card title="Clinical notes & plan" action={<Btn onClick={saveClinical} data-testid="workspace-save-clinical">SAVE</Btn>}>
                            <div className="space-y-4">
                                <Field label="Observations"><Textarea rows={3} value={clinical.observations || ""} data-testid="clinical-observations"
                                    onChange={(e) => setClinical((c) => ({ ...c, observations: e.target.value }))} /></Field>
                                <Field label="Assessment"><Textarea rows={3} value={clinical.assessment || ""} data-testid="clinical-assessment"
                                    onChange={(e) => setClinical((c) => ({ ...c, assessment: e.target.value }))} /></Field>
                                <Field label="Traditional medicine guidance"><Textarea rows={3} value={clinical.traditional_medicine || ""}
                                    data-testid="clinical-traditional"
                                    onChange={(e) => setClinical((c) => ({ ...c, traditional_medicine: e.target.value }))} /></Field>
                                <div className="grid sm:grid-cols-2 gap-4">
                                    <Field label="Diet advice"><Textarea rows={3} value={clinical.diet_advice || ""} data-testid="clinical-diet"
                                        onChange={(e) => setClinical((c) => ({ ...c, diet_advice: e.target.value }))} /></Field>
                                    <Field label="Lifestyle advice"><Textarea rows={3} value={clinical.lifestyle_advice || ""} data-testid="clinical-lifestyle"
                                        onChange={(e) => setClinical((c) => ({ ...c, lifestyle_advice: e.target.value }))} /></Field>
                                </div>
                                <Field label="Product recommendations"><Textarea rows={2} value={clinical.product_recommendations || ""}
                                    data-testid="clinical-products"
                                    onChange={(e) => setClinical((c) => ({ ...c, product_recommendations: e.target.value }))} /></Field>
                                <Field label="Plan shared with the patient" hint="This is the only clinical text the patient can see.">
                                    <Textarea rows={4} value={clinical.patient_summary || ""} data-testid="clinical-patient-summary"
                                        onChange={(e) => setClinical((c) => ({ ...c, patient_summary: e.target.value }))} /></Field>
                                <div className="grid sm:grid-cols-2 gap-4">
                                    <Field label="Follow-up date"><Input type="date" value={clinical.follow_up_date || ""} data-testid="clinical-follow-up"
                                        onChange={(e) => setClinical((c) => ({ ...c, follow_up_date: e.target.value }))} /></Field>
                                    <Field label="Private notes (staff only)"><Input value={clinical.private_notes || ""} data-testid="clinical-private"
                                        onChange={(e) => setClinical((c) => ({ ...c, private_notes: e.target.value }))} /></Field>
                                </div>
                            </div>
                        </Card>
                    )}

                    {can("messaging.view") && (
                        <Card title="Patient conversation">
                            <div className="space-y-3 max-h-[320px] overflow-y-auto pr-1" data-testid="workspace-chat">
                                {messages.length === 0 && <div className="text-sm text-zinc-500">No messages yet.</div>}
                                {messages.map((m) => (
                                    <div key={m.id} className={`max-w-[85%] px-4 py-3 text-sm ${
                                        m.sender_type === "doctor" ? "ml-auto bg-gold/10 border border-gold/25" : "bg-white/5 border border-white/10"
                                    }`}>
                                        <div className="mono text-[9px] tracking-[0.16em] text-zinc-500">{m.sender_name?.toUpperCase()} · {dt(m.created_at)}</div>
                                        <div className="mt-1.5 whitespace-pre-wrap">{m.body}</div>
                                    </div>
                                ))}
                            </div>
                            {can("messaging.create") && (
                                <div className="mt-4 flex gap-2">
                                    <Input value={draft} onChange={(e) => setDraft(e.target.value)} data-testid="workspace-chat-input"
                                        placeholder="Reply to the patient…" onKeyDown={(e) => e.key === "Enter" && send()} />
                                    <Btn onClick={send} data-testid="workspace-chat-send"><Send className="w-3.5 h-3.5" /></Btn>
                                </div>
                            )}
                        </Card>
                    )}

                    <Card title="Timeline">
                        <ul className="space-y-2 text-xs text-zinc-400">
                            {(b.history || []).map((h, i) => (
                                <li key={i} className="flex gap-3">
                                    <span className="text-zinc-600">{dt(h.at)}</span>
                                    <span className="text-zinc-200">{h.by}</span>
                                    <span>{h.action}</span>
                                    <span className="text-zinc-500">{h.detail}</span>
                                </li>
                            ))}
                        </ul>
                    </Card>
                </div>
            </div>
        </div>
    );
};

// ---------------------------------------------------------------- availability
const AvailabilityPanel = ({ canManage }) => {
    const qc = useQueryClient();
    const { data: rows = [] } = useQuery({
        queryKey: ["consult-availability"],
        queryFn: async () => (await rhm.get("/consult/availability")).data,
    });
    const [doctorId, setDoctorId] = useState("");
    const [cfg, setCfg] = useState(null);

    useEffect(() => {
        if (!rows.length) return;
        const id = doctorId || rows[0].doctor_id;
        setDoctorId(id);
        setCfg(rows.find((r) => r.doctor_id === id) || null);
    }, [rows, doctorId]);

    const save = useMutation({
        mutationFn: () => rhm.put(`/consult/availability/${doctorId}`, {
            slot_minutes: Number(cfg.slot_minutes) || 30, buffer_minutes: Number(cfg.buffer_minutes) || 0,
            max_per_day: Number(cfg.max_per_day) || 0, lead_time_hours: Number(cfg.lead_time_hours) || 0,
            horizon_days: Number(cfg.horizon_days) || 21, is_active: !!cfg.is_active,
            holidays: (cfg.holidays || []).filter(Boolean), days: cfg.days || {},
        }),
        onSuccess: () => { toast.success("Availability saved"); qc.invalidateQueries({ queryKey: ["consult-availability"] }); },
        onError: (e) => toast.error(apiError(e)),
    });

    if (!cfg) return <Card title="Availability"><div className="text-sm text-zinc-500">No consultants found.</div></Card>;

    const setWindow = (day, idx, patch) => {
        const days = { ...(cfg.days || {}) };
        const list = [...(days[day] || [])];
        list[idx] = { ...list[idx], ...patch };
        days[day] = list;
        setCfg({ ...cfg, days });
    };
    const addWindow = (day) => {
        const days = { ...(cfg.days || {}) };
        days[day] = [...(days[day] || []), { start: "10:00", end: "13:00" }];
        setCfg({ ...cfg, days });
    };
    const removeWindow = (day, idx) => {
        const days = { ...(cfg.days || {}) };
        days[day] = (days[day] || []).filter((_, i) => i !== idx);
        setCfg({ ...cfg, days });
    };

    return (
        <Card title="Consultant availability" data-testid="availability-panel" action={
            <div className="flex gap-2">
                <Select value={doctorId} data-testid="availability-doctor-select"
                    onChange={(e) => { setDoctorId(e.target.value); setCfg(rows.find((r) => r.doctor_id === e.target.value)); }}
                    options={rows.map((r) => ({ value: r.doctor_id, label: r.doctor_name }))} />
                {canManage && <Btn onClick={() => save.mutate()} data-testid="availability-save">SAVE</Btn>}
            </div>
        }>
            <div className="grid sm:grid-cols-3 lg:grid-cols-6 gap-4">
                <Field label="Slot minutes"><Input type="number" value={cfg.slot_minutes} data-testid="availability-slot-minutes"
                    onChange={(e) => setCfg({ ...cfg, slot_minutes: e.target.value })} /></Field>
                <Field label="Buffer minutes"><Input type="number" value={cfg.buffer_minutes} data-testid="availability-buffer"
                    onChange={(e) => setCfg({ ...cfg, buffer_minutes: e.target.value })} /></Field>
                <Field label="Max per day"><Input type="number" value={cfg.max_per_day} data-testid="availability-max"
                    onChange={(e) => setCfg({ ...cfg, max_per_day: e.target.value })} /></Field>
                <Field label="Lead time (hours)"><Input type="number" value={cfg.lead_time_hours} data-testid="availability-lead"
                    onChange={(e) => setCfg({ ...cfg, lead_time_hours: e.target.value })} /></Field>
                <Field label="Booking horizon (days)"><Input type="number" value={cfg.horizon_days} data-testid="availability-horizon"
                    onChange={(e) => setCfg({ ...cfg, horizon_days: e.target.value })} /></Field>
                <Field label="Accepting bookings">
                    <Select value={cfg.is_active ? "yes" : "no"} data-testid="availability-active"
                        onChange={(e) => setCfg({ ...cfg, is_active: e.target.value === "yes" })}
                        options={[{ value: "yes", label: "Yes" }, { value: "no", label: "No" }]} />
                </Field>
            </div>

            <div className="mt-7 space-y-4">
                {WEEKDAYS.map((day) => (
                    <div key={day} className="border border-white/10 p-4" data-testid={`availability-day-${day}`}>
                        <div className="flex items-center justify-between">
                            <div className="mono text-[10px] tracking-[0.2em] text-zinc-400">{day.toUpperCase()}</div>
                            <Btn variant="ghost" onClick={() => addWindow(day)} data-testid={`availability-add-${day}`}>
                                <Plus className="w-3 h-3 inline mr-1" /> WINDOW
                            </Btn>
                        </div>
                        {(cfg.days?.[day] || []).length === 0
                            ? <div className="text-xs text-zinc-500 mt-3">Closed</div>
                            : <div className="mt-3 space-y-2">
                                {(cfg.days[day] || []).map((w, i) => (
                                    <div key={i} className="flex items-center gap-3">
                                        <Input value={w.start} className="max-w-[120px]" data-testid={`availability-${day}-start-${i}`}
                                            onChange={(e) => setWindow(day, i, { start: e.target.value })} />
                                        <span className="text-zinc-500">to</span>
                                        <Input value={w.end} className="max-w-[120px]" data-testid={`availability-${day}-end-${i}`}
                                            onChange={(e) => setWindow(day, i, { end: e.target.value })} />
                                        <button onClick={() => removeWindow(day, i)} data-testid={`availability-remove-${day}-${i}`}
                                            className="text-zinc-500 hover:text-red-400"><Trash2 className="w-4 h-4" /></button>
                                    </div>
                                ))}
                            </div>}
                    </div>
                ))}
            </div>

            <div className="mt-6">
                <Field label="Holidays / leave (comma separated YYYY-MM-DD)">
                    <Input value={(cfg.holidays || []).join(", ")} data-testid="availability-holidays"
                        onChange={(e) => setCfg({ ...cfg, holidays: e.target.value.split(",").map((s) => s.trim()) })} />
                </Field>
            </div>
        </Card>
    );
};

// ---------------------------------------------------------------- notifications
const NotificationsPanel = () => {
    const qc = useQueryClient();
    const { data: rows = [] } = useQuery({
        queryKey: ["consult-notifications"],
        queryFn: async () => (await rhm.get("/consult/notifications")).data,
        refetchInterval: 30000,
    });
    const read = async (id) => {
        await rhm.post(`/consult/notifications/${id}/read`);
        qc.invalidateQueries({ queryKey: ["consult-notifications"] });
    };
    return (
        <Card title={`Notifications (${rows.filter((r) => !r.is_read).length} unread)`} data-testid="notifications-panel">
            {rows.length === 0 ? <div className="text-sm text-zinc-500">Nothing yet.</div> : (
                <ul className="space-y-2">
                    {rows.map((n) => (
                        <li key={n.id} data-testid={`notification-${n.id}`}
                            className={`flex items-start gap-3 border px-4 py-3 ${n.is_read ? "border-white/8 opacity-60" : "border-gold/30 bg-gold/[0.05]"}`}>
                            <Bell className="w-4 h-4 text-gold mt-0.5 shrink-0" />
                            <div className="flex-1">
                                <div className="text-sm text-white">{n.title}</div>
                                <div className="text-xs text-zinc-400 mt-0.5">{n.body}</div>
                                <div className="mono text-[9px] tracking-[0.14em] text-zinc-600 mt-1">{dt(n.created_at)}</div>
                            </div>
                            {!n.is_read && <Btn variant="ghost" onClick={() => read(n.id)} data-testid={`notification-read-${n.id}`}>MARK READ</Btn>}
                        </li>
                    ))}
                </ul>
            )}
        </Card>
    );
};
